'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import {
  User as FirebaseUser,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  updatePassword as firebaseUpdatePassword,
  updateProfile as firebaseUpdateProfile,
  EmailAuthProvider,
  reauthenticateWithCredential,
  browserLocalPersistence,
  onIdTokenChanged,
  setPersistence,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import { doc, getDoc, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import type { User as AppUser } from '@/types';
import { auth, db, firebaseWebConfig } from '@/lib/firebase/config';

function stripUndefined<T extends Record<string, unknown>>(obj: T): T {
  const cleanedEntries = Object.entries(obj).filter(([, value]) => value !== undefined);
  return Object.fromEntries(cleanedEntries) as T;
}

let cachedAuthorizedDomains: string[] | null = null;

async function getAuthorizedDomains(): Promise<string[]> {
  if (cachedAuthorizedDomains) return cachedAuthorizedDomains;

  const apiKey = firebaseWebConfig.apiKey;
  if (!apiKey) return [];

  const response = await fetch(
    `https://identitytoolkit.googleapis.com/v1/projects?key=${encodeURIComponent(apiKey)}`,
    { method: 'GET' }
  );
  if (!response.ok) return [];

  const data = (await response.json()) as { authorizedDomains?: string[] };
  cachedAuthorizedDomains = data.authorizedDomains ?? [];
  return cachedAuthorizedDomains;
}

interface AuthContextType {
  // App user profile stored in Firestore (/users/{uid})
  user: AppUser | null;
  // Raw Firebase Auth user
  firebaseUser: FirebaseUser | null;
  isLoading: boolean;
  signInWithGoogle: () => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, name?: string) => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updateMyProfile: (updates: {
    name?: string;
    bio?: string;
    location?: string;
    website?: string;
    company?: string;
    avatarUrl?: string;
  }) => Promise<void>;
  changeMyPassword: (currentPassword: string, newPassword: string) => Promise<void>;
  signOut: () => Promise<void>;
  logout: () => Promise<void>;
  claims: Record<string, unknown> | null;
  refreshClaims: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  firebaseUser: null,
  isLoading: true,
  signInWithGoogle: async () => {},
  loginWithGoogle: async () => {},
  login: async () => {},
  signup: async () => {},
  resetPassword: async () => {},
  updateMyProfile: async () => {},
  changeMyPassword: async () => {},
  signOut: async () => {},
  logout: async () => {},
  claims: null,
  refreshClaims: async () => {},
});

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [user, setUser] = useState<AppUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [claims, setClaims] = useState<Record<string, unknown> | null>(null);

  useEffect(() => {
    // Ensure auth persists across refreshes and route transitions.
    // This is especially important for popup sign-in flows.
    setPersistence(auth, browserLocalPersistence).catch((err) => {
      console.warn('Auth persistence setup failed:', err);
    });

    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      setFirebaseUser(user);

      if (!user) {
        setUser(null);
        setClaims(null);
        setIsLoading(false);
        return;
      }

      const fallbackName = user.displayName || user.email?.split('@')[0] || 'User';
      const fallbackProfile: AppUser = {
        id: user.uid,
        email: user.email || '',
        name: fallbackName,
        role: 'student',
        // omit avatarUrl when missing (Firestore does not allow undefined)
        avatarUrl: user.photoURL || undefined,
        enrolledCourses: [],
        completedCourses: [],
        passedExams: [],
        createdAt: new Date().toISOString(),
      };

      // Important: set a provisional profile immediately so route guards that
      // rely on `user` don't redirect during the brief bootstrap window.
      setIsLoading(true);
      setUser((prev) => prev ?? fallbackProfile);

      try {
        const tokenResult = await user.getIdTokenResult();
        setClaims(tokenResult.claims);

        const userRef = doc(db, 'users', user.uid);

        // Firestore requests can occasionally fail right after popup sign-in
        // due to token propagation. Retry a couple of times before giving up.
        const maxAttempts = 3;
        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
          try {
            console.log('🔍 Loading user profile from Firestore:', user.uid, `(attempt ${attempt}/${maxAttempts})`);
            const snap = await getDoc(userRef);

            if (snap.exists()) {
              const userData = snap.data() as AppUser;
              console.log('✅ User profile loaded:', { email: userData.email, role: userData.role });
              setUser(userData);
              break;
            }

            console.log('⚠️ User profile not found, creating new student profile');
            await setDoc(
              userRef,
              stripUndefined({ ...fallbackProfile, createdAtServer: serverTimestamp() }),
              { merge: true }
            );
            console.log('✅ Created new user profile');
            setUser(fallbackProfile);
            break;
          } catch (attemptError) {
            const isLastAttempt = attempt === maxAttempts;
            console.warn('Profile load/create attempt failed:', attemptError);
            if (isLastAttempt) {
              throw attemptError;
            }
            // Backoff: 300ms, 900ms
            await new Promise((r) => setTimeout(r, 300 * attempt * attempt));
          }
        }
      } catch (err) {
        console.error('❌ Auth bootstrap failed:', err);
        console.error('Error details:', err instanceof Error ? err.message : err);
        // Do NOT force a "logout" UX here; keep a safe fallback profile so
        // the dashboard doesn't bounce to home while we debug rules/network.
        setUser(fallbackProfile);
      } finally {
        setIsLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const refreshClaims = async () => {
    if (firebaseUser) {
      const tokenResult = await firebaseUser.getIdTokenResult(true);
      setClaims(tokenResult.claims);
    }
  };

  const signInWithGoogle = async () => {
    try {
      const apiKey = firebaseWebConfig.apiKey;
      if (!apiKey) {
        throw new Error(
          'Firebase config error: NEXT_PUBLIC_FIREBASE_API_KEY is missing. Please restart the dev server after updating .env.local.'
        );
      }

      if (typeof window !== 'undefined') {
        const host = window.location.hostname;
        const authorizedDomains = await getAuthorizedDomains();
        if (authorizedDomains.length && !authorizedDomains.includes(host)) {
          throw new Error(
            `Firebase Auth blocked this domain (${host}). Add it in Firebase Console → Authentication → Settings → Authorized domains, then try again.`
          );
        }
      }

      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error('Error signing in with Google:', error);
      throw error;
    }
  };

  const login = async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error('Error logging in:', error);
      throw error;
    }
  };

  const signup = async (email: string, password: string, name?: string) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Create user profile in Firestore immediately
      const userRef = doc(db, 'users', user.uid);
      const newUser: AppUser = {
        id: user.uid,
        email: user.email || email,
        name: name || user.displayName || email.split('@')[0],
        role: 'student',
        avatarUrl: user.photoURL || undefined,
        enrolledCourses: [],
        completedCourses: [],
        passedExams: [],
        createdAt: new Date().toISOString(),
      };
      
      await setDoc(userRef, stripUndefined({ ...newUser, createdAtServer: serverTimestamp() }));
      console.log('✅ User profile created during signup:', user.uid);
    } catch (error) {
      console.error('Error signing up:', error);
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const origin = typeof window !== 'undefined' ? window.location.origin : undefined;
      const url = origin ? `${origin}/reset-password` : undefined;

      await sendPasswordResetEmail(
        auth,
        email,
        url
          ? {
              url,
              // Keep the user on our app after clicking the email link.
              // (Firebase will include oobCode/mode in the redirect.)
              handleCodeInApp: true,
            }
          : undefined
      );
    } catch (error) {
      console.error('Error sending password reset email:', error);
      throw error;
    }
  };

  const updateMyProfile: AuthContextType['updateMyProfile'] = async (updates) => {
    const fbUser = auth.currentUser;
    if (!fbUser) throw new Error('Not signed in');

    const userRef = doc(db, 'users', fbUser.uid);

    const cleaned = stripUndefined({
      ...updates,
      updatedAt: new Date().toISOString(),
      updatedAtServer: serverTimestamp(),
    });

    // Update Firestore profile first (source of truth for app).
    // If the profile doc somehow doesn't exist, create a minimal one then apply updates.
    try {
      await updateDoc(userRef, cleaned);
    } catch (err: unknown) {
      const code = (err as { code?: string } | null)?.code;
      if (code !== 'not-found') throw err;

      const fallbackName = updates.name?.trim() || fbUser.displayName || fbUser.email?.split('@')[0] || 'User';
      const minimalProfile: AppUser = {
        id: fbUser.uid,
        email: fbUser.email || '',
        name: fallbackName,
        role: 'student',
        avatarUrl: updates.avatarUrl || fbUser.photoURL || undefined,
        enrolledCourses: [],
        completedCourses: [],
        passedExams: [],
        createdAt: new Date().toISOString(),
        bio: updates.bio,
        location: updates.location,
        website: updates.website,
        company: updates.company,
      };

      await setDoc(
        userRef,
        stripUndefined({ ...minimalProfile, ...updates, createdAtServer: serverTimestamp(), ...cleaned }),
        { merge: true }
      );
    }

    // Keep Firebase Auth displayName/photoURL in sync when updating profile.
    const displayName = updates.name?.trim();
    const photoURL = updates.avatarUrl?.trim();
    if (displayName || photoURL) {
      await firebaseUpdateProfile(fbUser, {
        ...(displayName ? { displayName } : {}),
        ...(photoURL ? { photoURL } : {}),
      });
    }

    // Update local state immediately so UI reflects changes without refresh.
    setUser((prev) => (prev ? ({ ...prev, ...updates } as AppUser) : prev));
  };

  const changeMyPassword: AuthContextType['changeMyPassword'] = async (
    currentPassword,
    newPassword
  ) => {
    const fbUser = auth.currentUser;
    const email = fbUser?.email;
    if (!fbUser || !email) throw new Error('Not signed in');

    const trimmedNew = newPassword.trim();
    if (trimmedNew.length < 6) {
      const err = new Error('Password should be at least 6 characters.') as Error & { code?: string };
      err.code = 'auth/weak-password';
      throw err;
    }

    const credential = EmailAuthProvider.credential(email, currentPassword);
    await reauthenticateWithCredential(fbUser, credential);
    await firebaseUpdatePassword(fbUser, trimmedNew);
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider 
      value={{ 
        user, 
        firebaseUser,
        isLoading,
        signInWithGoogle, 
        loginWithGoogle: signInWithGoogle,
        login,
        signup,
        resetPassword,
        updateMyProfile,
        changeMyPassword,
        signOut,
        logout: signOut,
        claims,
        refreshClaims,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
